import React, { ReactNode, createContext, useContext } from 'react';
import { create } from 'zustand';

import { useToggle } from '@/src/hooks/useToggle';

import { EmailEntry, EmailEntryListItem, EmailFileEntry, ReplyType } from '../../../../types';

type EmailThreadEntryViewerZustandStore = {
  replyType: ReplyType;
  emailEntryToReply: EmailEntry;
  selectedEmailEntry: EmailEntry;
  selectedEmailEntryListItem: EmailEntryListItem;
  emailFileEntry: EmailFileEntry;
  setEmailFileEntry: (emailFileEntry: EmailFileEntry) => void;
  selectEmailEntry: ({
    selectedEmailEntry,
    selectedEmailEntryListItem,
  }: {
    selectedEmailEntry: EmailEntry;
    selectedEmailEntryListItem: EmailEntryListItem;
  }) => void;
  selectEmailFileEntry: (selectedEmailEntry: EmailEntry, emailFileEntry: EmailFileEntry) => void;
  setEmailEntryToReply: (emailEntryToReply: EmailEntry) => void;
  replyToEmail: ({
    emailEntryToReply,
    selectedEmailEntryListItem,
    replyType,
  }: {
    emailEntryToReply: EmailEntry;
    selectedEmailEntryListItem: EmailEntryListItem;
    replyType: ReplyType;
  }) => void;
  setSelectedEmailEntry: (selectedEmailEntry: EmailEntry) => void;
  setSelectedEmailEntryListItem: (selectedEmailEntryListItem: EmailEntryListItem) => void;
  setReplyType: (replyType: ReplyType) => void;
  clearThreadEntryViewerStore: () => void;
};

type EmailThreadEntryViewerStore = EmailThreadEntryViewerZustandStore & {
  showSidePanel: boolean;
  toggleShowSidePanel: (showSidePanel?: boolean) => void;
  isFullScreen: boolean;
  toggleIsFullScreen: (show?: boolean | null) => void;
};

const EmailThreadEntryViewerContext = createContext<EmailThreadEntryViewerStore>(undefined);

const useEmailThreadEntryViewerStore = create<EmailThreadEntryViewerZustandStore>((set) => ({
  replyType: null,
  selectedEmailEntry: null,
  selectedEmailEntryListItem: null,
  emailEntryToReply: null,
  emailFileEntry: null,
  setEmailEntryToReply: (emailEntryToReply: EmailEntry) =>
    set(() => ({
      emailEntryToReply,
    })),
  replyToEmail: ({ emailEntryToReply, selectedEmailEntryListItem, replyType }) =>
    set(() => ({
      emailEntryToReply,
      selectedEmailEntryListItem,
      replyType,
    })),
  setSelectedEmailEntry: (selectedEmailEntry: EmailEntry) =>
    set(() => {
      return {
        selectedEmailEntry,
      };
    }),
  setSelectedEmailEntryListItem: (selectedEmailEntryListItem: EmailEntryListItem) =>
    set(() => ({
      selectedEmailEntryListItem,
    })),
  setEmailFileEntry: (emailFileEntry: EmailFileEntry) =>
    set(() => ({
      emailFileEntry,
    })),
  selectEmailEntry: ({ selectedEmailEntry, selectedEmailEntryListItem }) =>
    set(() => {
      return {
        selectedEmailEntry,
        selectedEmailEntryListItem,
      };
    }),
  selectEmailFileEntry: (selectedEmailEntry: EmailEntry, emailFileEntry: EmailFileEntry) =>
    set(() => ({
      selectedEmailEntry,
      emailFileEntry,
    })),
  setReplyType: (replyType: ReplyType) =>
    set(() => ({
      replyType,
    })),
  clearThreadEntryViewerStore: () =>
    set(() => ({
      replyType: null,
    })),
}));

type EmailThreadEntryViewerProviderProps = {
  children: ReactNode;
};

export const EmailThreadEntryViewerProvider = ({
  children,
}: EmailThreadEntryViewerProviderProps) => {
  const store = useEmailThreadEntryViewerStore();
  const [showSidePanel, toggleShowSidePanel] = useToggle(false);
  const [isFullScreen, toggleIsFullScreen] = useToggle(false);

  return (
    <EmailThreadEntryViewerContext.Provider
      value={{
        ...store,
        showSidePanel,
        toggleShowSidePanel,
        isFullScreen,
        toggleIsFullScreen,
      }}
    >
      {children}
    </EmailThreadEntryViewerContext.Provider>
  );
};

export function useEmailThreadEntryViewer() {
  const store = useContext(EmailThreadEntryViewerContext);

  if (!store) {
    throw new Error(
      'useEmailThreadEntryViewer must be used within a EmailThreadEntryViewerProvider'
    );
  }
  return {
    ...store,
  };
}
